import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

interface SpansStats {
  totalCalls: number;
  successCalls: number;
  failedCalls: number;
  successRate: number;
  avgDuration: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  callsByProvider: Record<string, number>;
  callsByOperation: Record<string, number>;
  recentCalls: number;
}

interface StatsResponse {
  operations: {
    total: number;
    byType: Record<string, number>;
    byStatus: Record<string, number>;
  };
  spans: SpansStats;
}

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  openrouter: 'OpenRouter',
  xunfei: '讯飞',
  google: 'Google',
  deepseek: 'DeepSeek',
  other: '其他',
};

const OPERATION_LABELS: Record<string, string> = {
  pipeline_clean: '精简整理',
  pipeline_creative: '灵感升华',
  chat: '对话',
  summary: '摘要生成',
  translation: '翻译',
  transcription: '语音转文字',
  text_processing: '文字优化',
  chat_completion: '对话生成',
};

export default function Dashboard() {
  const { data: stats, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const response = await api.get<{
        status: string;
        code: number;
        data: StatsResponse;
      }>('/admin/operations/stats');
      console.log('[Dashboard] API Response:', response.data);

      const spansData = response.data.data.spans;
      const operationsData = response.data.data.operations;

      return {
        totalActivities: spansData.totalCalls,
        successActivities: spansData.successCalls,
        failedActivities: spansData.failedCalls,
        successRate: spansData.successRate,
        avgDuration: spansData.avgDuration,
        totalTokens: spansData.totalTokens,
        promptTokens: spansData.promptTokens,
        completionTokens: spansData.completionTokens,
        activitiesByProvider: spansData.callsByProvider || {},
        activitiesByOperation: spansData.callsByOperation || {},
        recentActivities: spansData.recentCalls || 0,
        operationsTotal: operationsData.total,
        operationsByStatus: operationsData.byStatus,
      };
    },
    staleTime: 30000, // 30秒缓存
    refetchOnWindowFocus: true,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">仪表盘</h1>
          <p className="mt-1 text-sm text-gray-600">
            AI 服务使用统计概览
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          刷新数据
        </button>
      </div>

      {isError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">
            ⚠️ 无法加载统计数据，请检查后端服务是否正常运行
          </p>
        </div>
      )}

      {/* 主要指标卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 总操作数 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">总操作次数</p>
              <p className="mt-2 text-3xl font-bold text-gray-900">
                {stats?.totalActivities || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            总操作记录: {stats?.operationsTotal || 0} 条
          </div>
        </div>

        {/* 成功率 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">成功率</p>
              <p className="mt-2 text-3xl font-bold text-green-600">
                {(stats?.successRate ?? 0).toFixed(1)}%
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            成功: {stats?.successActivities || 0} / 失败: {stats?.failedActivities || 0}
          </div>
        </div>

        {/* 平均耗时 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">平均耗时</p>
              <p className="mt-2 text-3xl font-bold text-purple-600">
                {Math.round(stats?.avgDuration || 0)}ms
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-xs text-gray-500">
            {(stats?.avgDuration || 0) > 0
              ? `约 ${Math.round((stats?.avgDuration || 0) / 1000)} 秒`
              : '-'}
          </div>
        </div>

        {/* Token 消耗 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Token 消耗</p>
              <p className="mt-2 text-3xl font-bold text-orange-600">
                {stats?.totalTokens || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 space-y-1">
            {(stats?.promptTokens || stats?.completionTokens) ? (
              <>
                <div className="text-xs text-gray-600">
                  输入: {stats?.promptTokens || 0} • 输出: {stats?.completionTokens || 0}
                </div>
              </>
            ) : (
              <div className="text-xs text-gray-500">
                本次统计周期内
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 详细统计 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 按操作类型统计 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            📊 按操作类型
          </h3>
          <div className="space-y-4">
            {stats?.activitiesByOperation && Object.keys(stats.activitiesByOperation).length > 0 ? (
              Object.entries(stats.activitiesByOperation)
                .sort(([, a], [, b]) => b - a)
                .map(([operation, count]) => {
                  const percentage = ((count / (stats.totalActivities || 1)) * 100).toFixed(1);
                  return (
                    <div key={operation} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">
                          {OPERATION_LABELS[operation] || operation}
                        </span>
                        <span className="text-sm text-gray-500">
                          {count} 次 ({percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-300 group-hover:from-blue-600 group-hover:to-blue-700"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                暂无数据
              </p>
            )}
          </div>
        </div>

        {/* 按 AI 提供商 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            🔌 按 AI 提供商
          </h3>
          <div className="space-y-4">
            {stats?.activitiesByProvider && Object.keys(stats.activitiesByProvider).length > 0 ? (
              Object.entries(stats.activitiesByProvider)
                .sort(([, a], [, b]) => b - a)
                .map(([provider, count]) => {
                  const percentage = ((count / (stats.totalActivities || 1)) * 100).toFixed(1);
                  return (
                    <div key={provider} className="group">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">
                          {PROVIDER_LABELS[provider] || provider}
                        </span>
                        <span className="text-sm text-gray-500">
                          {count} 次 ({percentage}%)
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full transition-all duration-300 group-hover:from-green-600 group-hover:to-green-700"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 mb-2">
                  暂无数据
                </p>
                <p className="text-xs text-gray-400">
                  提供商统计功能正在开发中
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 快捷操作 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          🚀 快捷操作
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/user-operations"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <div className="font-medium text-gray-900">会员操作记录</div>
              <div className="text-sm text-gray-500">查看所有操作历史</div>
            </div>
          </Link>

          <Link
            to="/users"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
          >
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <div>
              <div className="font-medium text-gray-900">用户管理</div>
              <div className="text-sm text-gray-500">管理会员账号</div>
            </div>
          </Link>

          <Link
            to="/admins"
            className="flex items-center p-4 border border-gray-200 rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors"
          >
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
              <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <div className="font-medium text-gray-900">管理员</div>
              <div className="text-sm text-gray-500">管理管理员账号</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
